import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Icon,
  Paper,
  Box,
  Card,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';

// --- FAKE DATA (No changes needed) ---
const FAKE_ORCHESTRATORS_DATA = [
  {
    productName: 'Billing Service',
    productFulfillmentScenarios: [
      {
        recordId: '1axHr000000PD0oIAG',
        fields: { Action: 'Add' },
        fulfillmentStepDefnGroupName: 'Billing',
        Name: 'Billing Scenario',
      },
      {
        recordId: '1axHr000000PD0vIAG',
        fields: { Action: 'Amend' },
        fulfillmentStepDefnGroupName: 'Billing',
        Name: 'Ecommerce (Amend) Scenario',
      },
    ],
    fulfillmentStepDefinitions: [
      {
        recordId: '0qDHr000000CfB3MAK',
        fields: { StepType: 'Milestone', Scope: 'Plan', AssignedToId: null },
        stepDefinitionGroupName: 'Billing',
        Name: 'Fulfilled',
      },
      {
        recordId: '0qDHr000000kDTDMA2',
        fields: { StepType: 'ManualTask', Scope: 'Plan', AssignedToId: '005Hr00000Hmll4IAB' }, // Matches assignment rules
        stepDefinitionGroupName: 'Billing',
        Name: 'Initiate Billing',
      },
      {
        recordId: '0qDHr000000kDTIMA2',
        fields: { StepType: 'Callout', Scope: 'CrossPlan', AssignedToId: null },
        stepDefinitionGroupName: 'Billing',
        Name: 'New Booking',
      },
    ],
    fulfillmentJeopardyRules: [
      {
        recordId: '1AmHr000000sZUhKAM',
        fields: {
          StepType: 'Callout',
          Name: 'Jeopardy-Callout-API-Timeout',
          JeopardyThresholdUnit: 'Seconds',
          JeopardyThreshold: 30,
          EstimatedDurationUnit: 'Seconds',
          EstimatedDuration: 5,
        },
      },
      {
        recordId: '1AmHr000000sZUrXYZ',
        fields: {
          StepType: 'ManualTask',
          Name: 'Jeopardy-Manual-SLA',
          JeopardyThresholdUnit: 'Hours',
          JeopardyThreshold: 4,
          EstimatedDurationUnit: 'Hours',
          EstimatedDuration: 1,
        },
      },
      {
        recordId: '1AmHr000000sZUrABC',
        fields: {
          StepType: 'ManualTask',
          Name: 'Jeopardy-Manual-First-Response',
          JeopardyThresholdUnit: 'Minutes',
          JeopardyThreshold: 15,
          EstimatedDurationUnit: 'Minutes',
          EstimatedDuration: 5,
        },
      },
    ],
    fulfillmentFalloutRules: [
      {
        recordId: '1AgHr00000001GTKAY',
        fields: {
          RetriesAllowed: 3,
          StepType: 'Callout',
          RetryPolicy: 'Exponential Backoff',
          Name: 'Fallout-Callout-API-Error',
        },
      },
      {
        recordId: '1AgHr00000001GTABC',
        fields: {
          RetriesAllowed: 1,
          StepType: 'ManualTask',
          RetryPolicy: 'Immediate',
          Name: 'Fallout-Manual-Validation',
        },
      },
    ],
  },
];
const FAKE_ASSIGNMENT_RULES_DATA = [
  {
    recordId: '2AmHr0000004C9DKAU',
    fields: { Priority: 2, Name: 'Automated Booking', DestinationId: '005Hr00000HeDAdIAN' },
    sourceName: 'System',
    destinationName: 'Automated Process',
  },
  {
    recordId: '2AmHr0000004C93KAE',
    fields: { Priority: 1, Name: 'Primary Billing Assignment', DestinationId: '005Hr00000Hmll4IAB' },
    sourceName: 'Director, Direct Sales',
    destinationName: 'Martina Topalovic',
  },
  {
    recordId: '2AmHr0000004C98KAE',
    fields: { Priority: 2, Name: 'Secondary Billing Assignment', DestinationId: '005Hr00000Hmll4IAB' },
    sourceName: 'VP of Finance',
    destinationName: 'Billing Team Queue',
  },
];
// --- END OF FAKE DATA ---

// --- FIX: The sx prop is removed from TableHead to make tables visible ---
const RuleTable = ({ title, rules, fieldsToShow = [], customRender = {} }) => {
  if (!rules || rules.length === 0) {
    return (
      <ArgonBox mt={2}>
        <ArgonTypography variant="subtitle2" fontWeight="bold" mb={1}>
          {title}
        </ArgonTypography>
        <ArgonTypography variant="caption" color="textSecondary">
          No rules defined for this step.
        </ArgonTypography>
      </ArgonBox>
    );
  }

  const headers =
    fieldsToShow.length > 0 ? fieldsToShow : [...Object.keys(rules[0].fields), ...Object.keys(customRender)];

  return (
    <ArgonBox mt={2}>
      <ArgonTypography variant="subtitle2" fontWeight="bold" mb={1} ml={1}>
        {title}
      </ArgonTypography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead sx={{ display: 'contents' }}>
            <TableRow>
              {headers.map((field) => (
                <TableCell key={field}>
                  <ArgonTypography variant="caption" fontWeight="bold">
                    {field}
                  </ArgonTypography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rules.map((rule) => (
              <TableRow key={rule.recordId}>
                {headers.map((field) => (
                  <TableCell key={`${rule.recordId}-${field}`}>
                    {customRender[field] ? customRender[field](rule) : rule.fields[field] ?? 'N/A'}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </ArgonBox>
  );
};

function FulfillmentTab({ product, fieldsConfig = {} }) {
  const combineFulfillmentData = (orchestrators, assignmentRules) => {
    if (!orchestrators || orchestrators.length === 0) return [];
    if (!assignmentRules || assignmentRules.length === 0) {
      return orchestrators.map((orch) => ({
        ...orch,
        fulfillmentStepDefinitions: orch.fulfillmentStepDefinitions.map((step) => ({ ...step, assignmentRules: [] })),
      }));
    }
    const rulesByDestination = new Map();
    for (const rule of assignmentRules) {
      const destId = rule.fields.DestinationId;
      if (!destId) continue;
      if (!rulesByDestination.has(destId)) rulesByDestination.set(destId, []);
      rulesByDestination.get(destId).push(rule);
    }
    return orchestrators.map((orch) => ({
      ...orch,
      fulfillmentStepDefinitions: orch.fulfillmentStepDefinitions.map((step) => ({
        ...step,
        assignmentRules: rulesByDestination.get(step.fields.AssignedToId) || [],
      })),
    }));
  };

  const [expandedScenario, setExpandedScenario] = useState({});
  const [expandedStep, setExpandedStep] = useState({});
  const [expandedRuleSection, setExpandedRuleSection] = useState({});

  const combinedOrchestrators = useMemo(() => {
    const orchestrators = product?.related_data?.dynamicRevenueOrchestrators || FAKE_ORCHESTRATORS_DATA;
    const assignmentRules = product?.related_data?.fulfillmentTaskAssignmentRules || FAKE_ASSIGNMENT_RULES_DATA;
    return combineFulfillmentData(orchestrators, assignmentRules);
  }, [product]);

  const handleToggle = (setter, key) => {
    setter((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleRuleSectionToggle = (key) => {
    setExpandedRuleSection((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (!combinedOrchestrators || combinedOrchestrators.length === 0) {
    return (
      <ArgonBox p={3}>
        <ArgonTypography variant="body2">No Fulfillment data available for this product.</ArgonTypography>
      </ArgonBox>
    );
  }

  return (
    <ArgonBox p={2}>
      {combinedOrchestrators.map((orchestrator, orchestratorIndex) => (
        <Box key={orchestrator.productId || orchestratorIndex}>
          {orchestrator.productFulfillmentScenarios.map((scenario) => {
            const scenarioId = scenario.recordId;
            const stepsInScenario = orchestrator.fulfillmentStepDefinitions.filter(
              (step) => step.stepDefinitionGroupName === scenario.fulfillmentStepDefnGroupName
            );

            return (
              <Card key={scenarioId} sx={{ mb: 2 }}>
                <ArgonBox
                  mb={0.5}
                  mt={0.5}
                  p={1}
                  borderRadius="md"
                  onClick={() => handleToggle(setExpandedScenario, scenarioId)}
                  sx={{ cursor: 'pointer' }}
                >
                  <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                    <ArgonTypography variant="h6" fontWeight="medium" color="secondary">
                      {scenario.Name} ({scenario.fields.Action})
                    </ArgonTypography>
                    <Icon>{expandedScenario[scenarioId] ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
                  </ArgonBox>
                </ArgonBox>
                <Collapse in={expandedScenario[scenarioId]} timeout="auto" unmountOnExit>
                  <ArgonBox pl={2}>
                    {stepsInScenario.map((step) => {
                      const stepId = step.recordId;
                      // --- FIX: Create a globally unique key for each step ---
                      const compositeStepKey = `${scenarioId}-${stepId}`;

                      const jeopardyRulesForStep = orchestrator.fulfillmentJeopardyRules.filter(
                        (rule) => rule.fields.StepType === step.fields.StepType
                      );
                      const falloutRulesForStep = orchestrator.fulfillmentFalloutRules.filter(
                        (rule) => rule.fields.StepType === step.fields.StepType
                      );

                      return (
                        <Card key={stepId} sx={{ mb: 1 }}>
                          <ArgonBox
                            mb={0.5}
                            mt={0.5}
                            p={1}
                            borderRadius="md"
                            // --- FIX: Use the unique composite key to toggle state ---
                            onClick={() => handleToggle(setExpandedStep, compositeStepKey)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                              <ArgonTypography variant="body2" fontWeight="medium">
                                Step: {step.Name} ({step.fields.StepType})
                              </ArgonTypography>
                              {/* --- FIX: Use the unique composite key to check state --- */}
                              <Icon>
                                {expandedStep[compositeStepKey] ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                              </Icon>
                            </ArgonBox>
                          </ArgonBox>
                          {/* --- FIX: Use the unique composite key to control collapse --- */}
                          <Collapse in={expandedStep[compositeStepKey]} timeout="auto" unmountOnExit>
                            <ArgonBox p={2}>
                              {/* This part was already correct, but now it will work as intended */}
                              <RuleTable
                                title="Assignment Rules"
                                rules={step.assignmentRules}
                                fieldsToShow={fieldsConfig.assignmentRuleFields}
                                customRender={{
                                  sourceName: (rule) => rule.sourceName,
                                  destinationName: (rule) => rule.destinationName,
                                }}
                              />
                              <RuleTable
                                title="Jeopardy Rules"
                                rules={jeopardyRulesForStep}
                                fieldsToShow={fieldsConfig.jeopardyRuleFields}
                              />
                              <RuleTable
                                title="Fallout Rules"
                                rules={falloutRulesForStep}
                                fieldsToShow={fieldsConfig.falloutRuleFields}
                              />
                            </ArgonBox>
                          </Collapse>
                        </Card>
                      );
                    })}
                  </ArgonBox>
                </Collapse>
              </Card>
            );
          })}
        </Box>
      ))}
    </ArgonBox>
  );
}

export default FulfillmentTab;
